import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';
import { QrImage } from '../components/QrImage';

interface TableRow {
  id: string;
  label: string;
  seats: number;
  qr_token: string;
  is_floating: boolean;
}

function dinerUrl(token: string): string {
  return `${window.location.origin}/t/${token}`;
}

export function TablesPage() {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];

  const [tables, setTables] = useState<TableRow[]>([]);
  const [label, setLabel] = useState('');
  const [seats, setSeats] = useState('2');
  const [floating, setFloating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!restaurant) return;
    const { data } = await supabase
      .from('tables')
      .select('id, label, seats, qr_token, is_floating')
      .eq('restaurant_id', restaurant.id)
      .order('label');
    if (data) setTables(data as TableRow[]);
  }, [restaurant]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function addTable(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!restaurant) return;
    const { error: err } = await supabase
      .from('tables')
      .insert({ restaurant_id: restaurant.id, label: label.trim(), seats: Number(seats) || 2, is_floating: floating });
    if (err) return setError(err.message);
    setLabel('');
    setSeats('2');
    setFloating(false);
    void reload();
  }

  async function deleteTable(id: string) {
    await supabase.from('tables').delete().eq('id', id);
    void reload();
  }

  async function downloadQr(table: TableRow) {
    const url = await QRCode.toDataURL(dinerUrl(table.qr_token), {
      width: 600,
      margin: 2,
      color: { dark: '#2b2723', light: '#fbf7f0' }
    });
    const a = document.createElement('a');
    a.href = url;
    a.download = `mesa-${table.label}.png`;
    a.click();
  }

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  return (
    <div className="page">
      <h1 className="page__title">{t('tables.title')}</h1>

      <form onSubmit={addTable} className="form inline-form">
        <div className="form-row">
          <label className="field">
            <span>{t('tables.label')}</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} required />
          </label>
          <label className="field">
            <span>{t('tables.seats')}</span>
            <input type="number" min={1} value={seats} onChange={(e) => setSeats(e.target.value)} />
          </label>
        </div>
        <label className="adj">
          <input type="checkbox" checked={floating} onChange={(e) => setFloating(e.target.checked)} />
          {t('tables.floating')}
        </label>
        <span className="muted-note">{t('tables.floating_hint')}</span>
        {error && <p className="form-error">{error}</p>}
        <button type="submit" className="btn btn--primary">
          {t('tables.add')}
        </button>
      </form>

      {tables.length === 0 ? (
        <p className="muted-note">{t('tables.none')}</p>
      ) : (
        <div className="qr-grid">
          {tables.map((tb) => (
            <div key={tb.id} className="qr-card">
              <div className="qr-card__head">
                <strong>{tb.label}</strong>
                {tb.is_floating
                  ? <span className="tag">{t('tables.floating_badge')}</span>
                  : <span className="tag">{tb.seats} {t('tables.seats').toLowerCase()}</span>}
              </div>
              <QrImage value={dinerUrl(tb.qr_token)} size={140} />
              <div className="qr-card__actions">
                <button type="button" className="link-button" onClick={() => void downloadQr(tb)}>
                  {t('tables.download')}
                </button>
                <button type="button" className="link-button" onClick={() => void deleteTable(tb.id)}>
                  {t('tables.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
